"use client";

import * as React from "react";
import {useEffect, useMemo, useRef, useState} from "react";
import {useTheme} from "@mui/material/styles";
import {Box, Collapse, List, ListItemButton} from "@mui/material";
import {debounce} from "@/utils/debounce";
import {useGetSuggestionsQuery} from "@/redux/apis";
import {LayoutSearchFilterProps} from "@/types/layout";
import {Field} from "@/components/form";


export default function LayoutSearchFilter({
                                               searchText,
                                               setSearchText,
                                               productsPage,
                                               onSubmitSearch,
                                           }: LayoutSearchFilterProps) {
    const theme = useTheme();
    const [isOpen, setIsOpen] = useState(false);
    const containerRef = useRef<HTMLDivElement | null>(null);
    const [debouncedSearchText, setDebouncedSearchText] = useState(searchText);
    const [highlightedIndex, setHighlightedIndex] = useState(-1);
    const listItemRefs = useRef<(HTMLDivElement | null)[]>([]);
    const queryText = debouncedSearchText.trim()?.length > 1 ? debouncedSearchText : undefined;

    const {data: predictions} = useGetSuggestionsQuery(queryText as any);

    const debouncedUpdate = useMemo(() => debounce((value: string) => setDebouncedSearchText(value), 1000), []);

    useEffect(() => {
        debouncedUpdate(searchText);
        return () => debouncedUpdate.clear();
    }, [searchText, debouncedUpdate]);


    useEffect(() => {
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                setIsOpen(false);
                setHighlightedIndex(-1);
            }
        };
        document.addEventListener("click", handleClickOutside);
        return () => document.removeEventListener("click", handleClickOutside);
    }, []);

    useEffect(() => {
        if (highlightedIndex >= 0 && listItemRefs.current[highlightedIndex]) {
            listItemRefs.current[highlightedIndex]?.scrollIntoView({
                block: "nearest",
                behavior: "smooth",
            });
        }
    }, [highlightedIndex]);

    const handleSelectPrediction = (prediction: string) => {
        setSearchText(prediction);
        setIsOpen(false);
        setHighlightedIndex(-1);
        onSubmitSearch?.(prediction);
    };

    const handleKeyDown = (e: React.KeyboardEvent<HTMLInputElement>) => {
        if (e.key === "Tab") {
            setIsOpen(false);
            setHighlightedIndex(-1);
            return;
        }

        if (!predictions || !predictions?.length) {
            if (e.key === "Enter") {
                e.preventDefault();
                setIsOpen(false);
                setHighlightedIndex(-1);
                onSubmitSearch?.(searchText);
            }
            return;
        }

        if (e.key === "ArrowDown") {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev < predictions?.length - 1 ? prev + 1 : 0));
        } else if (e.key === "ArrowUp") {
            e.preventDefault();
            setHighlightedIndex((prev) => (prev > 0 ? prev - 1 : predictions?.length - 1));
        } else if (e.key === "Enter") {
            e.preventDefault();
            if (highlightedIndex >= 0) {
                handleSelectPrediction(predictions[highlightedIndex]);
            } else {
                onSubmitSearch?.(searchText);
            }
        } else if (e.key === "Escape") {
            setIsOpen(false);
            (e.currentTarget as HTMLInputElement).blur();
        }
    };


    return (
        <Box ref = {containerRef} position= "relative" width= "100%" height= "100%" >
            <Field.Text
                sx={{
                    height: "100%",
                    ...( !productsPage && {
                        "& .MuiOutlinedInput-root.Mui-hovered fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 1.5,
                        },
                        "& .MuiOutlinedInput-root": {
                            "&:hover": {
                                "& > fieldset": {
                                    borderColor: theme.palette.primary.main,
                                    borderWidth: 1.5,
                                }
                            },
                            "&.Mui-focused": {
                                "& > fieldset": {
                                    borderColor: theme.palette.primary.main,
                                    borderWidth: 1.5,
                                }
                            }
                        },
                        "& .MuiOutlinedInput-root.Mui-focused fieldset": {
                            borderColor: theme.palette.primary.main,
                            borderWidth: 1.5,
                        },
                    }),
                }}
                type = "search"
                closeIcon
                searchIcon={productsPage}
                onClickAdornment = {() => {
                    setSearchText("");
                    setIsOpen(false);
                }}
                fullWidth
                placeholder = {productsPage ? `نام یا کد قطعه را وارد کنید...`
                    : `تمام یا بخشی از نام قطعه یا کد‌فنی را وارد کنید...`}
                size = "small"
                value = {searchText}
                onFocus = {() => setIsOpen(true)}
                onBlur = {() => {
                    setIsOpen(false);
                    setHighlightedIndex(-1);
                }}
                onChange = {(e) => {
                    setSearchText(e.target.value);
                    setIsOpen(true);
                    setHighlightedIndex(-1);
                }}
                onKeyDown = {handleKeyDown}
            />

            <Collapse in = {isOpen && !!predictions?.length} >
                <Box
                    position= "absolute"
                    width= "100%"
                    bgcolor={theme.palette.common.white}
                    mt={1}
                    height={180}
                    zIndex={10}
                    border={`1px solid ${theme.palette.grey[400]}`}
                    sx = {{
                        overflowY: "auto",
                    }}
                >
                    {predictions && (
                        <List >
                            {predictions
                                .filter((p) => p !== searchText)
                                .map((prediction, index) => (
                                    <ListItemButton
                                        key = {prediction}
                                        ref={(el) => {
                                            listItemRefs.current[index] = el;
                                        }}
                                        onClick = {() => handleSelectPrediction(prediction)}
                                        selected = {index === highlightedIndex}
                                        sx = {{display: "flex", justifyContent: "space-between"}}
                                    >
                                        {prediction}
                                    </ListItemButton >
                                ))}
                        </List >
                    )}
                </Box >
            </Collapse >
        </Box >
    );
}
